/**
 * @file OrderDeliveryBlock.tsx
 * @description Вкладка «Доставка» карточки заказа — единственный дом условий
 * доставки (phase19.24 шаг 5.2, проект
 * `phase19.24-step5-2-delivery-block-design.md` §2, §3).
 *
 * До шага 5.2 доставка жила в двух местах: расчёт СДЭК во вкладке «Отгрузки»
 * и согласование над вкладками. Менеджер считал тариф в одном месте, а цену
 * клиенту называл в другом — и они расходились. Теперь здесь всё: что
 * согласуем, чем посчитали, сколько платит клиент и каким письмом это ушло.
 *
 * Что показывать, решает реестр `delivery-block-state.ts`, а не условия в
 * разметке: бейдж, объясняющий текст и подпись главной кнопки приходят одной
 * записью — «Подтверждено» в шапке больше не может уживаться с формой
 * «Отправить на подтверждение» внизу.
 *
 * Данные о доставке приходят пропом: один `GET …/delivery` на карточку
 * (`OrderDetailPage`) обслуживает и эту вкладку, и «Отгрузки» — два ответа про
 * одни условия успевали разойтись.
 *
 * Нижней sticky-полосы у блока нет намеренно: главное действие заказа одно —
 * в верхней панели карточки (`CabinetPageHeader`, SC52), а на телефоне каркас
 * переносит его в общую нижнюю полосу; вторая фиксированная полоса блока
 * перекрывала бы её на 360px.
 */

import { useState } from "react";
import { Link } from "react-router";

import { DeliveryBlockCta } from "./DeliveryBlockCta";
import { DeliveryBlockHeader } from "./DeliveryBlockHeader";
import { DeliveryChangeConfirmDialog } from "./DeliveryChangeConfirmDialog";
import { DeliveryOutcomeEditor } from "./DeliveryOutcomeEditor";
import {
  useDeliveryAgreementActions,
  type AgreementSendRequest,
} from "./useDeliveryAgreementActions";
import type { OrderDeliveryState } from "../../../hooks/useOrderDelivery";
import type { DeliveryAgreementDto } from "../../../lib/api-delivery-agreement";
import type { OrderDto } from "../../../lib/api-orders";
import { isAgreementOverdue } from "../../../lib/delivery-block-state";
import { deliveryBlockView } from "../../../lib/order-delivery-view";
import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { DeliveryAgreementConfirmDialog } from "../DeliveryAgreementConfirmDialog";
import { DeliveryAgreementSentPanel } from "../DeliveryAgreementSentPanel";
import { OrderTabSkeleton } from "../OrderTabSkeleton";

export type { AgreementSendRequest };

interface Props {
  order: OrderDto;
  /** Заявка из карточки: запасной источник, пока `GET …/delivery` её не отдаёт. */
  agreement: DeliveryAgreementDto | null;
  /** Общий на карточку ответ `GET …/delivery` (условия, запрет, перевозчики). */
  delivery: OrderDeliveryState;
  canManage: boolean;
  shopSlug?: string;
  /** Полоса «Сейчас» позвала «Отправить на подтверждение». */
  sendRequest?: AgreementSendRequest | null;
  onSendHandled?: () => void;
  /** Перевести заказ в стадию согласования доставки (стадийный запрет). */
  onAgreeDelivery?: () => void;
  /** Переход стадии уже выполняется — кнопку не нажимают дважды. */
  acting?: boolean;
  /** Перечитать заказ и путь после действия. */
  onChanged: () => void | Promise<void>;
}

export function OrderDeliveryBlock({
  order,
  agreement: cardAgreement,
  delivery,
  canManage,
  shopSlug,
  sendRequest,
  onSendHandled,
  onAgreeDelivery,
  acting,
  onChanged,
}: Props) {
  /** Менеджер открыл форму руками: «Изменить условия», «Изменить исход». */
  const [editing, setEditing] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  /** Код причины последнего неудачного расчёта (объясняет пустую стоимость). */
  const [quoteReason, setQuoteReason] = useState<string | null>(null);

  const data = delivery.data;
  // Один источник правды: состояние, бейдж и суммы считает `deliveryBlockView`
  // по ответу сервера (заявка из него же, карточная — запасная).
  const { agreement, receipt, state, meta, badge, text, itemsTotal } = deliveryBlockView(
    order,
    data,
    cardAgreement,
    { canManage, editing, quoteReason },
  );

  async function refresh(): Promise<void> {
    setEditing(false);
    await delivery.reload();
    await onChanged();
  }

  const actions = useDeliveryAgreementActions(
    order.id,
    refresh,
    sendRequest,
    onSendHandled,
  );

  if (delivery.loading && !data) {
    return (
      // Заготовка высотой блока, а не спиннер: страница не прыгает (qa4 п. 14).
      <OrderTabSkeleton />
    );
  }

  const sent = state === "SENT" || state === "OVERDUE";
  // Письмо уже уходило: подписи диалогов и пункт «Отменить заявку» зависят
  // от этого, и знать об этом должны оба ответа.
  const resend = !!agreement?.calculatedAt;
  const busy = actions.busy || !!acting;
  const recipientIssues = data?.recipientIssues ?? [];

  return (
    <EditorialCard>
      <EditorialCard.Body className="flex flex-col gap-4">
        <DeliveryBlockHeader
          meta={meta}
          badge={badge}
          text={text}
          receipt={receipt}
          agreement={agreement}
          siteOrder={order.source === "SITE"}
          loadError={delivery.error}
        />

        {/* F16 (шаг 5.3): чего не хватает получателю под способ — подсветка,
            не блокировка; чинится в карточке клиента. */}
        {recipientIssues.length > 0 && (meta.form || sent) ? (
          <NoticeStrip
            tone="warning"
            title="Данные получателя"
            action={
              <Button variant="outline" size="sm" className="min-h-11" asChild>
                <Link to={`/shop/${shopSlug ?? ""}/customers/${order.customerId}`}>
                  Заполнить в карточке клиента
                </Link>
              </Button>
            }
          >
            {recipientIssues.map((i) => i.message).join(" ")}
          </NoticeStrip>
        ) : null}

        {/* Ход клиента: вместо формы — срок, ссылка и три действия. */}
        {sent && agreement ? (
          <DeliveryAgreementSentPanel
            agreement={agreement}
            overdue={isAgreementOverdue(agreement)}
            disabled={busy}
            onEdit={() => setEditing(true)}
            onResend={() => actions.open("send")}
            onCancelRequest={() => actions.open("cancel")}
          />
        ) : null}

        <DeliveryBlockCta
          meta={meta}
          disabled={busy}
          onChange={() => setChangeOpen(true)}
          onAgree={onAgreeDelivery}
        />

        {meta.form ? (
          <DeliveryOutcomeEditor
            orderId={order.id}
            receipt={receipt}
            paymentTerms={data?.paymentTerms ?? agreement?.paymentTerms}
            itemsTotal={itemsTotal}
            legalEntity={order.buyerKind === "LEGAL_ENTITY" || !!order.legalEntityId}
            shopSlug={shopSlug}
            primaryLabel={meta.primaryLabel}
            integratedCarriers={data?.integratedCarriers ?? []}
            delivery={data}
            canCancel={resend}
            disabled={busy}
            onSaved={refresh}
            onQuoteReason={setQuoteReason}
            onSend={() => actions.open("send")}
            onCancelRequest={() => actions.open("cancel")}
          />
        ) : null}
      </EditorialCard.Body>

      <DeliveryAgreementConfirmDialog
        kind={actions.openKind}
        resend={resend}
        totalGross={agreement?.totals.totalGross ?? order.totalGross}
        busy={actions.busy}
        onClose={actions.close}
        onConfirm={(kind) => void actions.run(kind)}
      />

      <DeliveryChangeConfirmDialog
        open={changeOpen}
        fromSite={receipt?.source === "SITE" && !resend}
        onClose={() => setChangeOpen(false)}
        onConfirm={() => {
          setChangeOpen(false);
          setEditing(true);
        }}
      />
    </EditorialCard>
  );
}
